// filepath: resources/js/components/pages/CMSPages/FriVia/Results/QuizQuestionAnswersChart.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CollapseBlock from '../../../../common/blocks/CollapseBlock/CollapseBlock.jsx';
import { Pie } from 'react-chartjs-2';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * Zobrazuje koláčový graf rozloženia odpovedí pre jednu otázku (question mode)
 * Vstup: pole rows z endpointu aggregatedStudentQuizCorrectAnswers (vetva fv_question_id)
 * Očakáva stĺpec student_answers_text (odpovede študenta spojené ' | ')
 */
const QuizQuestionAnswersChart = ({ rows }) => {
  const { t } = useTranslation();

  const { labels, values, total } = useMemo(() => {
    const counts = {};
    rows.forEach((r) => {
      const raw = r.student_answers_text;
      if (!raw) return;
      raw.split('|').forEach((part) => {
        const trimmed = part.trim();
        if (!trimmed) return;
        counts[trimmed] = (counts[trimmed] || 0) + 1;
      });
    });
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const top = entries.slice(0, 5);
    const topSum = top.reduce((acc, [, c]) => acc + c, 0);
    const total = entries.reduce((acc, [, c]) => acc + c, 0);
    const labels = top.map((e) => e[0]);
    const values = top.map((e) => e[1]);
    const other = total - topSum;
    if (other > 0) {
      labels.push(t('Ostatné'));
      values.push(other);
    }
    return { labels, values, total };
  }, [rows, t]);

  if (!labels.length) {
    return (
      <CollapseBlock title={t('Distribúcia odpovedí')} collapsible>
        <p>{t('Žiadne výsledky')}</p>
      </CollapseBlock>
    );
  }

  const palette = [
    '#FF6384',
    '#36A2EB',
    '#FFCE56',
    '#4BC0C0',
    '#9966FF',
    '#FF9F40',
    '#F2F2F2B0',
  ];

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: palette.slice(0, values.length),
        hoverBackgroundColor: palette.slice(0, values.length),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = ctx.parsed || 0;
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return `${ctx.label}: ${value} (${pct}%)`;
          },
        },
      },
    },
  };

  return (
    <CollapseBlock title={t('Distribúcia odpovedí')} collapsible>
      <div style={{ height: '400px' }}>
        <Pie data={data} options={options} />
      </div>
    </CollapseBlock>
  );
};

QuizQuestionAnswersChart.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default QuizQuestionAnswersChart;
